'use client'

import React, { useMemo } from 'react'
import { useTranslations } from 'next-intl'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined'
import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import HikingRoundedIcon from '@mui/icons-material/HikingRounded'
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined'
import MuseumOutlinedIcon from '@mui/icons-material/MuseumOutlined'
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined'
import TerrainRoundedIcon from '@mui/icons-material/TerrainRounded'

import {
    HOLIDAY_POPULAR_THEMES,
    HOLIDAY_THEME,
    HOLIDAY_THEME_LABEL_KEY,
    type HolidayTheme
} from '@/src/constants/holidays'
import type { HolidayPackageFacet, HolidayPackageFilters } from '@/src/utils/holidays/packageFilters'

import PackageThemeChips, { type ThemeChip } from './packageThemeChips'

interface PackageThemeChipsWrapperProps {
    themeFacets: HolidayPackageFacet[]
    filters: HolidayPackageFilters
    onToggle: (key: string) => void
    isMobile: boolean
}

const THEME_ICON: Record<string, React.ReactNode> = {
    [HOLIDAY_THEME.BEACH]: <BeachAccessOutlinedIcon />,
    [HOLIDAY_THEME.FAMILY]: <FamilyRestroomRoundedIcon />,
    [HOLIDAY_THEME.ADVENTURE]: <HikingRoundedIcon />,
    [HOLIDAY_THEME.ROMANTIC]: <FavoriteBorderRoundedIcon />,
    [HOLIDAY_THEME.CULTURE]: <MuseumOutlinedIcon />,
    [HOLIDAY_THEME.MOUNTAINS]: <TerrainRoundedIcon />,
    [HOLIDAY_THEME.CITY]: <ApartmentRoundedIcon />,
    [HOLIDAY_THEME.SOLO]: <AccountCircleOutlinedIcon />,
    [HOLIDAY_THEME.WELLNESS]: <SpaOutlinedIcon />,
    [HOLIDAY_THEME.SHORT]: <LuggageOutlinedIcon />
}

const PackageThemeChipsWrapper: React.FC<PackageThemeChipsWrapperProps> = ({
    themeFacets,
    filters,
    onToggle,
    isMobile
}) => {
    const t = useTranslations('holidaysPage')

    // The rail is a shortcut to the busiest styles; the filter rail carries the full list.
    const chips: ThemeChip[] = useMemo(
        () =>
            HOLIDAY_POPULAR_THEMES.flatMap(theme => themeFacets.filter(facet => facet.key === theme)).map(facet => ({
                key: facet.key,
                label: t(HOLIDAY_THEME_LABEL_KEY[facet.key as HolidayTheme]),
                count: facet.count,
                selected: filters.themes.includes(facet.key),
                icon: THEME_ICON[facet.key]
            })),
        [themeFacets, filters.themes, t]
    )

    return (
        <PackageThemeChips chips={chips} onToggle={onToggle} groupLabel={t('themeGroupLabel')} isMobile={isMobile} />
    )
}

export { PackageThemeChipsWrapper }
export default PackageThemeChipsWrapper
